import * as THREE from "three";
import { MATS } from "./materials.js";
import { LOT_EDGES, LOTS, Z, HERO_LOT, benchY, groundY, insideFront } from "./spec.js";
import { makeRng } from "./builder.js";

/**
 * Garden dressing for the three front and three back yards -- and, because
 * this is a cat game, the level's climbing furniture.  Every standable prop
 * states its top height in a comment, and every route keeps each hop under
 * the 0.95 m jump (0.23 m is a plain step).
 *
 * Routes built here (see the b.mark() calls):
 *   route-bins-N   ground -> recycle box 0.42 -> wheelie-bin lid 1.11
 *   route-patio    chair 0.45 / planter 0.52 -> table 0.76; bbq 0.90 direct
 *   route-shed     crate 0.55 -> bin lid 1.11 -> log store 1.78 -> roof 2.61
 *   route-deck     steps 0.12/0.23/0.34 -> deck 0.45 -> rail cap 1.36
 *   route-hot-tub  deck 0.45 -> rim 0.93 (water stands at 0.86, 0.07 to rim)
 */

// ---------------------------------------------------------------------------
// the lot boundaries

/**
 * x, pulled back off whichever boundary fence it is nearest, so a crown of
 * `crown` metres cannot grow through the boards.
 *
 * fences.js builds the side fences from LOT_EDGES with a run collider
 * COL_T = 0.16 m thick centred on the edge, so the face a crown has to clear
 * is FENCE_FACE inside the line, and it is the crown that has to clear it --
 * not the trunk.  Planting here was authored by eye against the lot lines,
 * which was fine until spec.js tightened the subdivision by 0.75 m a side:
 * the fences moved (they read LOT_EDGES), the planting did not, and four
 * crowns were left growing through the outer boundary runs of lots 0 and 2.
 * Nothing in here types a boundary now.
 *
 * Only bites behind the houses.  The frontage is deliberately open -- there
 * is no fence to grow through, and front planting is clamped against the
 * STREET instead, by spec.js's insideFront.
 *
 * The same rule as hero/backspec.js's insideBoundary, which serves the hero
 * lot's own bed and screen.  It belongs in spec.js next to insideFront, and
 * neither of the changes that needed it owns that file.
 */
const FENCE_FACE = 0.08;   // fences.js COL_T / 2, which is private there
const LEAF_GAP = 0.06;     // daylight between crown and boards

function insideBoundary(x, z, crown) {
  if (z > Z.houseBack) return x;
  const clear = FENCE_FACE + LEAF_GAP + crown;
  for (const e of LOT_EDGES) {
    if (Math.abs(x - e) >= clear) continue;
    return x < e ? e - clear : e + clear;
  }
  return x;
}

// ---------------------------------------------------------------------------
// shared prop helpers

/** Clipped shrub: a squashed sphere plus a smaller offset lobe. Non-solid. */
function shrub(b, rng, x0, z, r, mat = MATS.hedge) {
  // The lobe sits 0.55 r out and is 0.62 r across, so the pair reaches 1.17 r
  // from the planting point -- that, and not r, is what has to clear a fence.
  const x = insideBoundary(x0, z, r * 1.17);
  b.sphere(x, 0, z, r, mat, { segments: 7, rings: 5, squash: 0.78 });
  const a = rng() * Math.PI * 2;
  b.sphere(x + Math.cos(a) * r * 0.55, 0, z + Math.sin(a) * r * 0.55,
           r * 0.62, mat, { segments: 6, rings: 4, squash: 0.8 });
}

/** A stylised flower clump: leaf ball with a colour head sunk into it. */
function flower(b, x, z, mat) {
  b.sphere(x, 0.02, z, 0.09, MATS.leafDark, { segments: 5, rings: 3, squash: 0.7 });
  b.sphere(x, 0.10, z, 0.07, mat, { segments: 5, rings: 3 });
}

/** Bed-edging rocks along a straight run. r <= 0.115 keeps the collider
 *  under the 0.23 m step height, so edging never blocks walking. */
function rockRow(b, rng, x0, z0, x1, z1, n) {
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    b.rock(x0 + (x1 - x0) * t + (rng() - 0.5) * 0.08,
           0, z0 + (z1 - z0) * t + (rng() - 0.5) * 0.08,
           0.085 + rng() * 0.03, MATS.stone);
  }
}

/** Stone edging around a circular bed. */
function rockRing(b, rng, x, z, rad, n) {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rng() * 0.3;
    b.rock(x + Math.cos(a) * rad, 0, z + Math.sin(a) * rad,
           0.085 + rng() * 0.03, MATS.stone);
  }
}

/** Street/garden tree with the reference photo's mulch ring + stone edge. */
function tree(b, rng, x0, z, s = 1, ring = true) {
  // Widest reach is a crown blob: 0.55 s offset plus a 0.8 s radius.  That is
  // more than the mulch ring's 1.16 s, so clearing the crown clears the ring.
  const x = insideBoundary(x0, z, 1.35 * s);
  if (ring) {
    b.post(x, 0.004, z, 1.05 * s, 0.05, MATS.mulch, { solid: false, segments: 14 });
    rockRing(b, rng, x, z, 1.16 * s, 9);
  }
  b.post(x, 0, z, 0.16 * s, 2.6 * s, MATS.bark, { topRadius: 0.11 * s, segments: 8 });
  b.sphere(x, 1.85 * s, z, 1.1 * s, MATS.leaf, { segments: 7, rings: 5, squash: 0.85 });
  b.sphere(x + 0.6 * s, 1.75 * s, z + 0.2 * s, 0.75 * s, MATS.leafDark, { segments: 6, rings: 4 });
  b.sphere(x - 0.55 * s, 1.9 * s, z - 0.25 * s, 0.8 * s, MATS.leafWarm, { segments: 6, rings: 4 });
}

/**
 * How far a piece must move to stand on the real ground rather than its bench.
 *
 * MODULE_GRADE.yards is benchY, which is right for everything in a back
 * garden -- that ground IS the bench.  The side passages are not: the ramp
 * falls through them, and benchY still reads 0 there, so anything placed down
 * a passage stands on a pad that no longer exists.  Cancel the bench and add
 * the terrain, the way fences.js does for a run.
 */
const lift = (x, z) => groundY(x, z) - benchY(x, z);

/**
 * Wheelie bin. Body top 1.04, lid top 1.11 above ITS OWN ground.
 *
 * Graded at the bin rather than by the module, because the bins live in the
 * side passage where the ramp is still falling: two bins 0.85 m apart sit 3 cm
 * of slope apart, and each standing on its own patch is what a row of bins on
 * a slope actually does.  The heights above are therefore relative -- the
 * route chain that reads them has to add the same lift.
 */
function bin(b, x, z, mat) {
  const dy = lift(x, z);
  b.block(x, 0.06 + dy, z, 0.55, 0.98, 0.62, mat);
  b.block(x, 1.04 + dy, z, 0.60, 0.07, 0.66, mat);
  b.sphere(x - 0.17, dy, z + 0.26, 0.07, MATS.metalDark, { segments: 5, rings: 3 });
  b.sphere(x + 0.17, dy, z + 0.26, 0.07, MATS.metalDark, { segments: 5, rings: 3 });
}

/** AC condenser against a house wall. Top 0.58. */
function acUnit(b, x, z) {
  b.block(x, 0, z, 0.68, 0.58, 0.36, MATS.metalPale);
  b.block(x, 0.58, z, 0.56, 0.02, 0.26, MATS.metalDark, { solid: false });
}

/** Coiled garden hose lying flat: two stacked tori. Non-solid. */
function hose(b, x, z) {
  for (const [r, y] of [[0.30, 0.02], [0.24, 0.10]]) {
    const g = new THREE.TorusGeometry(r, 0.05, 5, 10);
    g.rotateX(Math.PI / 2);
    g.translate(x, y + 0.05, z);
    b.add(g, MATS.plasticGreen, { tile: 0 });
  }
}

/** Stepping stones between two points, slight scatter. No colliders. */
function stones(b, rng, x0, z0, x1, z1, n) {
  for (let i = 0; i < n; i++) {
    const t = (i + 0.5) / n;
    b.slab(x0 + (x1 - x0) * t + (rng() - 0.5) * 0.5, 0.006,
           z0 + (z1 - z0) * t + (rng() - 0.5) * 0.4,
           0.4 + rng() * 0.14, 0.32 + rng() * 0.1, MATS.stone,
           { thickness: 0.028, tile: 0.6 });
  }
}

/** Planter box with a clipped ball on top. Box top is the standable bit. */
function planter(b, x, z, h, mat) {
  b.block(x, 0, z, 0.55, h, 0.55, mat);                       // top = h
  b.sphere(x, h - 0.08, z, 0.30, MATS.hedge, { segments: 6, rings: 4, squash: 0.85 });
}

// ---------------------------------------------------------------------------
// front gardens

function frontYard(b, rng, i) {
  const L = LOTS[i];
  const cx = L.centreX;

  // Foundation bed between the porch (ends at doorX + 2.2) and the driveway
  // (starts at garageX - 1.8): mulch, stone edge, clipped shrubs, flowers.
  const bedMinX = L.house.doorX + 2.35;
  const bedMaxX = L.house.garageX - 2.25;
  const bedCx = (bedMinX + bedMaxX) / 2, bedW = bedMaxX - bedMinX;
  b.slab(bedCx, 0.012, 2.35, bedW, 1.4, MATS.mulch, { thickness: 0.025 });
  rockRow(b, rng, bedMinX + 0.05, 3.12, bedMaxX - 0.05, 3.12, 8);
  shrub(b, rng, bedCx - bedW * 0.34, 2.15, 0.36);
  shrub(b, rng, bedCx + 0.05, 2.5, 0.44);
  shrub(b, rng, bedCx + bedW * 0.36, 2.1, 0.33);
  for (let f = 0; f < 4; f++) {
    flower(b, bedMinX + 0.3 + f * (bedW - 0.6) / 3, 2.85 + (rng() - 0.5) * 0.15,
           f % 2 ? MATS.flowerPale : MATS.flowerWarm);
  }

  // Street tree in lots 0 and 2, sited between the lot edge and the front
  // walk so it never lands on another module's paving.  Clamped inside the
  // front line by the reach of its own mulch ring: lot 0's authored spot is
  // metres clear and comes back untouched, while lot 2's -- where the crescent
  // brings the pavement nearly 3 m closer -- had half its ring of edging
  // stones sitting out on the sidewalk.
  // 1.6 of clearance is the tree's own mulch ring (1.16) plus half the depth
  // of whatever boundary is planted on the line, so the ring never runs into
  // the picket or the hedge either.
  const treeX = cx - 5.3 + (i - 1) * 0.2;
  const treeZ = insideFront(treeX, 9.2 + i * 0.6, 1.6);
  if (i !== 1) tree(b, rng, treeX, treeZ, 1);

  // Ornamental bed. Lot 1 gets the big one (it has no tree): a dwarf tree in
  // a mulch circle. Lots 0 and 2 get a small bed east of the driveway.
  if (i === 1) {
    b.post(cx - 5.3, 0.004, 9.0, 0.95, 0.05, MATS.mulch, { solid: false, segments: 14 });
    rockRing(b, rng, cx - 5.3, 9.0, 1.06, 8);
    b.post(cx - 5.3, 0, 9.0, 0.09, 1.35, MATS.barkDark, { topRadius: 0.06, segments: 7 });
    b.sphere(cx - 5.3, 1.1, 9.0, 0.62, MATS.leafDark, { segments: 7, rings: 5, squash: 0.9 });
    shrub(b, rng, cx - 4.6, 9.6, 0.3, MATS.leafWarm);
    flower(b, cx - 5.9, 9.7, MATS.flowerWarm);
    flower(b, cx - 4.7, 8.4, MATS.flowerPale);
  } else {
    // Porch-side front corner: clear of the driveway, the front walk zone
    // (doorX +/- ~1), the street tree's ring, and the lot boundary.  Clamped
    // inside the front line like the tree -- lot 0 barely moves, lot 2 was 2 m
    // out over the pavement with its edging stones in the gutter.
    //
    // Except that clamping can leave the corner too small to hold the bed AND
    // the tree.  Lot 0 has four metres of frontage past the lot line to play
    // with; the crescent gives lot 2 barely one, and pulling both features
    // back off the pavement stacks them 0.96 m apart when their two rings need
    // 1.84.  So the corner is tried first and the strip between the front walk
    // and the driveway apron -- the only other frontage this module owns -- is
    // the fallback, rather than quietly planting one bed inside another.
    const RING = 0.68, TREE_RING = 1.16;
    const corner = cx - 6.0;
    const spare = (L.house.doorX + 0.55 + L.house.garageX - 2.55) / 2;
    const fits = (x) => Math.hypot(x - treeX, insideFront(x, 13.0, RING + 0.47) - treeZ)
                        >= RING + TREE_RING;
    const bx = fits(corner) ? corner : spare;
    const bz = insideFront(bx, 13.0, RING + 0.47);   // 0.47: clear of the boundary
    b.post(bx, 0.004, bz, 0.56, 0.045, MATS.mulch, { solid: false, segments: 12 });
    rockRing(b, rng, bx, bz, RING, 6);
    shrub(b, rng, bx, bz, 0.34, MATS.leafWarm);
    flower(b, bx + 0.4, bz - 0.4, i ? MATS.flowerWarm : MATS.flowerPale);
  }
}

// ---------------------------------------------------------------------------
// shared back/side dressing: bins, AC, hose, per lot

function sideProps(b, i) {
  const L = LOTS[i];
  const cx = L.centreX;
  const bx = L.house.maxX + 0.37;   // bins hug the house's garage-side wall

  // route-bins: ground -> recycle box 0.42 -> bin lid 1.11 (steps 0.42, 0.69)
  bin(b, bx, -9.6, MATS.plasticGreen);
  bin(b, bx, -10.45, i === 1 ? MATS.plasticBlue : MATS.plasticGrey);
  b.block(bx, lift(bx, -8.82), -8.82, 0.5, 0.42, 0.42, MATS.plasticBlue);  // top 0.42
  b.mark(`route-bins-${i}`, bx, 1.11 + lift(bx, -9.6), -9.6);

  acUnit(b, cx + 4.6, -11.7);       // against the back wall, top 0.58
  hose(b, cx - 4.3, -12.4);
}

// ---------------------------------------------------------------------------
// back garden, lot 0: paver patio, table and chairs, bbq, planters

function patioChair(b, x, z, backDz) {
  b.post(x, 0, z, 0.05, 0.40, MATS.metalDark, { solid: false });
  b.block(x, 0.40, z, 0.48, 0.05, 0.48, MATS.timber);         // seat top 0.45
  b.block(x, 0.45, z + backDz, 0.48, 0.50, 0.06, MATS.timber); // backrest top 0.95
}

function backYard0(b, rng) {
  b.slab(-15.0, 0.012, -13.9, 5.6, 4.4, MATS.paver, { thickness: 0.025, tile: 0.8 });

  // Table top 0.76. Chairs: seat 0.45, backrest 0.95. Planter 0.52 sits a
  // short hop from the table edge. Every rise is 0.5 m or less.
  b.post(-15.6, 0, -14.6, 0.07, 0.70, MATS.metalDark);
  b.block(-15.6, 0.70, -14.6, 1.1, 0.06, 1.1, MATS.timber);
  b.post(-15.6, 0.76, -14.6, 0.035, 1.55, MATS.metalPale, { solid: false }); // parasol pole
  b.cone(-15.6, 2.05, -14.6, 1.25, 0.55, MATS.flowerWarm, { segments: 9 });
  patioChair(b, -16.75, -14.2, -0.24);
  patioChair(b, -15.2, -13.35, -0.24);
  planter(b, -14.55, -15.35, 0.52, MATS.timber);   // top 0.52 -> table 0.76
  planter(b, -17.55, -15.6, 0.50, MATS.plasticGreen);
  b.mark("route-patio", -15.6, 0.76, -14.6);

  // BBQ against the house end of the patio. Body top 0.90 -- a single max
  // jump from the ground; the 0.84 side shelf makes it comfortable.
  b.block(-17.1, 0.52, -12.5, 0.66, 0.38, 0.48, MATS.metalDark);
  b.post(-17.35, 0, -12.5, 0.035, 0.52, MATS.metalDark, { solid: false });
  b.post(-16.85, 0, -12.5, 0.035, 0.52, MATS.metalDark, { solid: false });
  b.block(-16.55, 0.80, -12.5, 0.35, 0.04, 0.44, MATS.timber); // shelf top 0.84
  b.bar(-17.1, 0.90, -12.26, 0.02, 0.4, "x", MATS.metalPale, { solid: false });

  // terracotta-less pot cluster by the wall, and a small fruit tree at the
  // back corner for shade
  b.post(-12.6, 0, -12.2, 0.16, 0.32, MATS.plasticGrey, { topRadius: 0.19, solid: false });
  flower(b, -12.6, -12.2, MATS.flowerWarm);
  b.post(-13.1, 0, -12.4, 0.12, 0.24, MATS.plasticGrey, { topRadius: 0.14, solid: false });
  tree(b, rng, -19.2, -26.0, 0.8, false);
  shrub(b, rng, -12.6, -28.3, 0.5, MATS.leafDark);
  shrub(b, rng, -17.6, -28.6, 0.42);
  stones(b, rng, -15.4, -16.6, -17.2, -23.6, 5);
}

// ---------------------------------------------------------------------------
// back garden, lot 1: lawn, shed, compost, wheelbarrow, washing line

function backYard1(b, rng) {
  // Shed 2.4 x 2.0, walls to 2.2, gable to 2.95. The roof's standable
  // collider tops out at 2.2 + 0.41 = 2.61.
  b.block(3.8, 0, -27.3, 2.4, 2.2, 2.0, MATS.timber);
  b.block(3.5, 0.02, -26.27, 0.75, 1.7, 0.06, MATS.trim, { solid: false });  // door
  b.block(5.0, 1.15, -27.3, 0.05, 0.5, 0.6, MATS.glass, { solid: false });   // window
  b.roof(3.8, 2.2, -27.3, 2.7, 2.3, 0.75, MATS.shingleAlt, { ridge: "x" });

  // route-shed, west side of the shed. Heights: crate 0.55 -> bin lid 1.11
  // -> log-store roof 1.78 -> shed roof 2.61. Steps 0.55/0.56/0.67/0.83.
  b.block(0.35, 0, -26.55, 0.55, 0.55, 0.55, MATS.timber);    // crate, top 0.55
  bin(b, 1.15, -27.35, MATS.plasticGreen);                    // lid 1.11
  b.block(2.2, 0, -27.3, 0.75, 1.70, 1.1, MATS.timber);       // log store
  b.block(2.2, 1.70, -27.3, 0.85, 0.08, 1.25, MATS.metalDark); // its roof, top 1.78
  b.block(1.80, 0.15, -27.3, 0.06, 1.3, 0.95, MATS.barkDark, { solid: false }); // stacked logs
  b.mark("route-shed", 0.35, 0.55, -26.55);

  // Washing line: two posts, a slack line, towels. Line is non-solid.
  b.post(-3.6, 0, -21.2, 0.05, 1.9, MATS.metalPale);
  b.post(0.4, 0, -21.2, 0.05, 1.9, MATS.metalPale);
  b.bar(-1.6, 1.72, -21.2, 0.015, 4.0, "x", MATS.metalPale, { solid: false });
  b.block(-2.6, 1.30, -21.2, 0.5, 0.45, 0.03, MATS.trim, { solid: false });
  b.block(-0.9, 1.32, -21.2, 0.45, 0.42, 0.03, MATS.flowerPale, { solid: false });

  // Compost bin (top 0.90, one max jump) and wheelbarrow (tray top 0.56).
  b.block(-5.9, 0, -28.4, 0.8, 0.9, 0.8, MATS.plasticGreen);
  b.cone(-5.9, 0.9, -28.4, 0.58, 0.2, MATS.plasticGreen, { segments: 4 });
  b.block(-2.3, 0.26, -25.6, 0.6, 0.30, 1.0, MATS.metalPale);
  b.sphere(-2.3, 0, -26.35, 0.15, MATS.metalDark, { segments: 6, rings: 4 });
  b.bar(-2.5, 0.40, -24.75, 0.02, 0.55, "z", MATS.timber, { solid: false });
  b.bar(-2.1, 0.40, -24.75, 0.02, 0.55, "z", MATS.timber, { solid: false });
  b.post(-2.5, 0, -25.15, 0.02, 0.26, MATS.metalDark, { solid: false });
  b.post(-2.1, 0, -25.15, 0.02, 0.26, MATS.metalDark, { solid: false });

  // Bird bath: pedestal + basin, explicit collider top 0.75 (direct jump).
  b.post(-4.4, 0, -23.4, 0.09, 0.62, MATS.concrete);
  b.sphere(-4.4, 0.58, -23.4, 0.30, MATS.stone, { segments: 7, rings: 4, squash: 0.3 });
  b.collider(-4.4, 0.62, -23.4, 0.5, 0.13, 0.5);

  stones(b, rng, 1.4, -12.4, 3.2, -25.7, 7);
  shrub(b, rng, -5.6, -13.0, 0.45, MATS.leafDark);
  shrub(b, rng, 5.9, -24.0, 0.4);
}

// ---------------------------------------------------------------------------
// back garden, lot 2: raised deck, hot tub, sandbox, toys

function backYard2(b, rng) {
  // Deck top 0.45; steps rise 0.12/0.11/0.11/0.11, all walkable.
  b.block(13.6, 0, -12.95, 4.6, 0.45, 2.8, MATS.timber);
  b.block(13.0, 0, -14.51, 1.2, 0.34, 0.32, MATS.timber);
  b.block(13.0, 0, -14.83, 1.2, 0.23, 0.32, MATS.timber);
  b.block(13.0, 0, -15.15, 1.2, 0.12, 0.32, MATS.timber);

  // Rail on the west edge: cap top 1.36 is a balance beam, 0.91 above the
  // deck -- just inside the jump. Posts are visual only.
  for (const z of [-11.8, -12.95, -14.1]) {
    b.post(11.42, 0.45, z, 0.035, 0.85, MATS.timber, { solid: false });
  }
  b.block(11.42, 1.30, -12.95, 0.12, 0.06, 2.75, MATS.timber); // cap top 1.36
  b.mark("route-deck", 13.6, 0.45, -12.95);

  // Hot tub 0.4 m east of the deck. Skirt fills the tub solid to 0.86 so the
  // cat stands "in the water" (drawn at 0.885) and steps 0.07 up onto the
  // 0.93 rim -- impossible to get trapped. Deck 0.45 -> rim 0.93 is 0.48.
  b.block(17.3, 0, -13.2, 2.0, 0.86, 2.0, MATS.timber);
  b.block(17.3, 0.86, -12.36, 2.12, 0.07, 0.32, MATS.plasticGrey);
  b.block(17.3, 0.86, -14.04, 2.12, 0.07, 0.32, MATS.plasticGrey);
  b.block(16.42, 0.86, -13.2, 0.32, 0.07, 1.36, MATS.plasticGrey);
  b.block(18.18, 0.86, -13.2, 0.32, 0.07, 1.36, MATS.plasticGrey);
  b.slab(17.3, 0.865, -13.2, 1.44, 1.36, MATS.glass, { thickness: 0.02, tile: 0.8 });
  b.mark("route-hot-tub", 17.3, 0.93, -13.2);

  // Planter on the deck (top 0.85): optional mid-step deck -> planter -> rim.
  planter(b, 15.35, -12.1, 0.40 + 0.45, MATS.timber);

  // Sandbox: frame top 0.22, solid sand fill top 0.14 -- 0.08 to walk out.
  b.block(18.6, 0, -16.85, 1.8, 0.22, 0.14, MATS.timber);
  b.block(18.6, 0, -18.35, 1.8, 0.22, 0.14, MATS.timber);
  b.block(17.77, 0, -17.6, 0.14, 0.22, 1.64, MATS.timber);
  b.block(19.43, 0, -17.6, 0.14, 0.22, 1.64, MATS.timber);
  b.block(18.6, 0, -17.6, 1.52, 0.14, 1.36, MATS.soil);
  b.post(18.95, 0.14, -17.35, 0.07, 0.13, MATS.plasticBlue, { topRadius: 0.09, solid: false });

  // toys on the lawn -- all non-solid clutter
  b.sphere(16.5, 0, -16.5, 0.14, MATS.plasticBlue, { segments: 6, rings: 4 });
  b.block(17.9, 0, -15.4, 0.32, 0.12, 0.18, MATS.plasticBlue, { solid: false });
  b.block(17.8, 0.12, -15.4, 0.12, 0.09, 0.16, MATS.plasticGrey, { solid: false });

  stones(b, rng, 13.0, -15.9, 17.6, -16.8, 4);
  shrub(b, rng, 12.4, -28.4, 0.46, MATS.leafDark);
  shrub(b, rng, 19.9, -28.7, 0.4);
  shrub(b, rng, 20.4, -15.8, 0.36, MATS.leafWarm);
}

// ---------------------------------------------------------------------------

export function build(b) {
  const rng = makeRng(0x9a2d5);   // constant seed: identical scatter each build
  for (let i = 0; i < 3; i++) {
    if (i !== HERO_LOT) frontYard(b, rng, i);   // hero/ plants the middle one
    sideProps(b, i);
  }
  backYard0(b, rng);
  // lot 1's back garden is rebuilt in detail from a photograph by hero/
  backYard2(b, rng);
}
